// components/FilterPresetSelector.jsx
import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, Star, Globe, User, Trash2 } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const FilterPresetSelector = ({
  presets,
  selectedPresetId,
  onSelectPreset,
  onDeletePreset,
  onSetDefault,
  currentUserId,
}) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedPreset = presets.find((p) => p.id === selectedPresetId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = async (e, presetId) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this filter preset?")) {
      await onDeletePreset(presetId);
    }
  };

  const handleSetDefault = async (e, presetId) => {
    e.stopPropagation();
    await onSetDefault(presetId);
  };

  const groupedPresets = {
    default: presets.filter(
      (p) => p.isDefault && p.createdBy === currentUserId,
    ),
    personal: presets.filter(
      (p) => !p.isPublic && !p.isDefault && p.createdBy === currentUserId,
    ),
    shared: presets.filter((p) => p.isPublic || p.scope !== "personal"),
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-2 border rounded transition-colors ${
          isDarkMode
            ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
            : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
        }`}
      >
        <span className="flex items-center gap-2">
          {selectedPreset ? (
            <>
              {selectedPreset.isDefault && (
                <Star size={16} className="text-yellow-400" />
              )}
              {selectedPreset.isPublic && (
                <Globe size={16} className="text-blue-400" />
              )}
              <span className="truncate">{selectedPreset.name}</span>
            </>
          ) : (
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Select a filter preset...
            </span>
          )}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform ${isOpen ? "rotate-180" : ""} ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute top-full left-0 right-0 mt-2 border rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Clear Selection */}
          <button
            onClick={() => {
              onSelectPreset(null);
              setIsOpen(false);
            }}
            className={`w-full px-4 py-2 text-left transition-colors border-b ${
              isDarkMode
                ? "text-gray-400 hover:bg-gray-700 border-gray-700"
                : "text-gray-600 hover:bg-gray-50 border-gray-200"
            }`}
          >
            Clear filter
          </button>

          {/* Default Preset */}
          {groupedPresets.default.length > 0 && (
            <div>
              <div
                className={`px-4 py-2 text-xs font-semibold uppercase ${
                  isDarkMode
                    ? "text-gray-500 bg-gray-800/50"
                    : "text-gray-400 bg-gray-50"
                }`}
              >
                Default
              </div>
              {groupedPresets.default.map((preset) => (
                <div
                  key={preset.id}
                  onClick={() => {
                    onSelectPreset(preset.id);
                    setIsOpen(false);
                  }}
                  className={`group flex items-center justify-between px-4 py-2 cursor-pointer transition-colors ${
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Star size={14} className="text-yellow-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium truncate ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {preset.name}
                      </div>
                      {preset.description && (
                        <div
                          className={`text-xs truncate ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {preset.description}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {selectedPresetId === preset.id && (
                      <Check size={16} className="text-green-400" />
                    )}
                    <button
                      onClick={(e) => handleDelete(e, preset.id)}
                      className={`p-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                        isDarkMode
                          ? "text-red-400 hover:text-red-300"
                          : "text-red-500 hover:text-red-600"
                      }`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Personal Presets */}
          {groupedPresets.personal.length > 0 && (
            <div>
              <div
                className={`px-4 py-2 text-xs font-semibold uppercase ${
                  isDarkMode
                    ? "text-gray-500 bg-gray-800/50"
                    : "text-gray-400 bg-gray-50"
                }`}
              >
                My Filters
              </div>
              {groupedPresets.personal.map((preset) => (
                <div
                  key={preset.id}
                  onClick={() => {
                    onSelectPreset(preset.id);
                    setIsOpen(false);
                  }}
                  className={`group flex items-center justify-between px-4 py-2 cursor-pointer transition-colors ${
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <User size={14} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium truncate ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {preset.name}
                      </div>
                      {preset.description && (
                        <div
                          className={`text-xs truncate ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {preset.description}
                        </div>
                      )}
                      <div
                        className={`text-xs mt-0.5 ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        Used {preset.usageCount || 0} times
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {selectedPresetId === preset.id && (
                      <Check size={16} className="text-green-400" />
                    )}
                    <button
                      onClick={(e) => handleSetDefault(e, preset.id)}
                      className={`p-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                        isDarkMode
                          ? "text-yellow-400 hover:text-yellow-300"
                          : "text-yellow-500 hover:text-yellow-600"
                      }`}
                      title="Set as default"
                    >
                      <Star size={14} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, preset.id)}
                      className={`p-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                        isDarkMode
                          ? "text-red-400 hover:text-red-300"
                          : "text-red-500 hover:text-red-600"
                      }`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Shared Presets */}
          {groupedPresets.shared.length > 0 && (
            <div>
              <div
                className={`px-4 py-2 text-xs font-semibold uppercase ${
                  isDarkMode
                    ? "text-gray-500 bg-gray-800/50"
                    : "text-gray-400 bg-gray-50"
                }`}
              >
                Shared Filters
              </div>
              {groupedPresets.shared.map((preset) => (
                <div
                  key={preset.id}
                  onClick={() => {
                    onSelectPreset(preset.id);
                    setIsOpen(false);
                  }}
                  className={`group flex items-center justify-between px-4 py-2 cursor-pointer transition-colors ${
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Globe size={14} className="text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium truncate ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {preset.name}
                      </div>
                      {preset.description && (
                        <div
                          className={`text-xs truncate ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {preset.description}
                        </div>
                      )}
                      <div
                        className={`text-xs mt-0.5 ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        {preset.scope === "company" ? "Company-wide" : "Team"} •
                        Used {preset.usageCount || 0} times
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {selectedPresetId === preset.id && (
                      <Check size={16} className="text-green-400" />
                    )}
                    {preset.createdBy === currentUserId && (
                      <button
                        onClick={(e) => handleDelete(e, preset.id)}
                        className={`p-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                          isDarkMode
                            ? "text-red-400 hover:text-red-300"
                            : "text-red-500 hover:text-red-600"
                        }`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {presets.length === 0 && (
            <div
              className={`px-4 py-8 text-center ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <p className="text-sm">No saved filter presets</p>
              <p className="text-xs mt-1">
                Create your first preset to get started
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterPresetSelector;
